import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { WorkflowApiService } from './workflow-api.service';
import * as workflowActions from './workflow.actions';
import * as workflowSelectors from './workflow.selectors';
import * as datasetSelectors from '../datasets/dataset.selectors';
import * as fromRouting from '../../../core/store/routing';
import { WorkflowRuleEvent, WorkflowRuleScheduleTrigger, WorkflowStepAction, WorkflowStepRule } from './workflow.model';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class WorkflowEffects {
  constructor(
    private actions$: Actions,
    private workflowApiService: WorkflowApiService,
    private store$: Store<any>
  ) {}

  getWorkflowForDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.getWorkflowForDataset),
      switchMap(action => {
        return this.workflowApiService.getWorkflowForDataset(action.datasetUId).pipe(
          map(results => {
            const workflow = results?.data[0];

            // ensure the array order matches the
            // sort orders for steps and actions
            workflow?.workflowSteps?.sort((a, b) => a.sortOrder - b.sortOrder);
            workflow?.workflowSteps?.map(step => {
              step?.actions?.sort((a, b) => a.sortOrder - b.sortOrder);
              step?.rules?.sort((a, b) => a.sortOrder - b.sortOrder);
              step?.rules?.map(rule => rule?.actions?.sort((a, b) => a.sortOrder - b.sortOrder));
            });

            return workflowActions.getWorkflowForDatasetComplete({ workflow, skipPagesValidation: action.skipPagesValidation });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  getStepsForDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.setWorkflowStepForDelete),
      withLatestFrom(
        this.store$.select(workflowSelectors.selectSelectedWorkflow),
        this.store$.select(datasetSelectors.selectSelectedDatasetUID)
      ),
      switchMap(([action, workflow, datasetUId]) => {
        return this.workflowApiService.getWorkflowStepsForDeletion(workflow.uId, action.workflowStep.uId, datasetUId).pipe(
          map(results => {
            return workflowActions.setWorkflowStepsForDeleteComplete({ deleteStep: action.workflowStep, results });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  saveSelectedWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.saveSelectedWorkflow),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        return [workflowActions.setIsWorking({ isWorking: true }), workflowActions.updateWorkflow({ workflow })];
      })
    )
  );

  updateWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.updateWorkflow),
      switchMap(action => {
        return this.workflowApiService.updateWorkflow(action.workflow).pipe(
          map(workflow => {
            return workflowActions.updateWorkflowComplete({ workflow });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  editSelectedWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.editSelectedWorkflow),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      map(([action, workflow]) => {
        const workflowUId = workflow.uId;

        return fromRouting.go({
          routeData: { path: ['management', 'workflow', workflowUId] }
        });
      })
    )
  );

  // Rules
  addWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.addWorkflowStepRule),
      switchMap(action => {
        let maxSortOrder = action.step.rules?.length;
        const rule: WorkflowStepRule = {
          uId: uuidv4(),
          name: '',
          ruleEvent: WorkflowRuleEvent.StepStarted,
          scheduleTrigger: WorkflowRuleScheduleTrigger.StepStarted,
          sortOrder: maxSortOrder,
          actions: []
        };
        return [workflowActions.setSelectedWorkflowStepRule({ step: action.step, rule })];
      })
    )
  );

  editWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.editWorkflowStepRule),
      map(action => {
        return workflowActions.setSelectedWorkflowStepRule({ step: action.step, rule: action.rule });
      })
    )
  );

  saveWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.saveWorkflowStepRule),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      map(([action, workflow]) => {
        let newWorkflow = null;
        const step = workflow.workflowSteps.find(s => s.uId === action.step.uId);

        if (!step.rules?.find(r => r.uId === action.rule.uId)) {
          newWorkflow = produce(workflow, draftState => {
            const step = draftState.workflowSteps.find(ws => ws.uId === action.step.uId);
            let sortOrder = 0;
            step.rules.map(a => (a.sortOrder = sortOrder++));
            step.rules.push(action.rule);
          });
        } else {
          newWorkflow = produce(workflow, draftState => {
            const step = draftState.workflowSteps.find(ws => ws.uId === action.step.uId);
            const ruleIndex = step.rules.findIndex(a => a.uId === action.rule.uId);
            step.rules[ruleIndex] = action.rule;
          });
        }
        return workflowActions.setUpdatedWorkflow({ workflow: newWorkflow });
      })
    )
  );

  deleteSelectedWorkflowStepRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.deleteSelectedWorkflowStepRule),
      withLatestFrom(
        this.store$.select(workflowSelectors.selectSelectedWorkflow),
        this.store$.select(workflowSelectors.selectSelectedWorkflowStep),
        this.store$.select(workflowSelectors.selectWorkflowStepRuleForDelete)
      ),
      map(([action, workflow, workflowStep, rule]) => {
        const newWorkflow = produce(workflow, draftState => {
          const step = draftState.workflowSteps.find(s => s.uId === workflowStep.uId);
          const ruleToRemove = step.rules.findIndex(a => a.uId === rule.uId);
          step.rules.splice(ruleToRemove, 1);
        });
        return workflowActions.setUpdatedWorkflow({ workflow: newWorkflow });
      })
    )
  );

  loadWorkflowRuleImplementations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.loadWorkflowRuleImplementations),
      switchMap(action => {
        return this.workflowApiService.getRuleImplementations().pipe(
          map(results => {
            return workflowActions.loadWorkflowRuleImplementationsComplete({ ruleImplementations: results });
          }),
          catchError(err => of(workflowActions.setError({ err })))
        );
      })
    )
  );

  addWizardView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.addWizardView),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        const newWorkflow = produce(workflow, draftState => {
          draftState.views.push(action.view);
        });

        return [workflowActions.getWorkflowForDatasetComplete({ workflow: newWorkflow }), workflowActions.setIsDirty({ isDirty: true })];
      })
    )
  );

  deleteWizardView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.deleteWizardView),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        const newWorkflow = produce(workflow, draftState => {
          const viewToRemove = draftState.views.findIndex(vw => vw.uId === action.view.uId);
          draftState.views.splice(viewToRemove, 1);

          const views = draftState.views?.filter(vw => vw.name.startsWith('wizard'));
          let index = 1;
          views.map(v => (v.name = `wizard-${index++}`));
          views.sort((a, b) => (a.name > b.name ? 1 : -1));
        });

        return [workflowActions.getWorkflowForDatasetComplete({ workflow: newWorkflow }), workflowActions.setIsDirty({ isDirty: true })];
      })
    )
  );

  moveWizardView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowActions.moveWizardView),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        const newWorkflow = produce(workflow, draftState => {
          let views = draftState.views?.filter(vw => vw.name.startsWith('wizard')).sort((a, b) => (a.name > b.name ? 1 : -1));
          const viewToMove = views.splice(action.previousIndex, 1);
          views.splice(action.currentIndex, 0, ...viewToMove);

          let index = 1;
          views.map(v => (v.name = `wizard-${index++}`));
          views.sort((a, b) => (a.name > b.name ? 1 : -1));
        });

        return [workflowActions.getWorkflowForDatasetComplete({ workflow: newWorkflow }), workflowActions.setIsDirty({ isDirty: true })];
      })
    )
  );
}
